import { request } from "../../../services/api.ts"
import { type BaseApiResponse } from "../../../types/api.ts"
import { buildFilterParams } from "../redux/globalFilters.ts"
import type {
  GlobalFilters,
  NbaItem,
  PortfolioBundle,
  PortfolioWindow
} from "../redux/portfolio.types.ts"
import type {
  ResolvedWidget,
  WidgetCatalog,
  WidgetInstanceV4,
  WidgetsResponse
} from "../redux/widgets.types.ts"
import type { DashboardStats } from "../types/types.ts"

const BASE_URL = "/user/users"

const PATHS = {
  getStats: `${BASE_URL}/home/`,
  portfolio: "/dashboard/portfolio/",
  widgets: "/dashboard/widgets/",
  resolve: "/dashboard/widgets/resolve/",
  catalog: "/dashboard/catalog/",
  nba: "/dashboard/nba/",
  exportPdf: "/dashboard/export.pdf",
  exportXlsx: "/dashboard/export.xlsx"
}

export const getDashboardStats = (team?: string) =>
  request.api.get<DashboardStats, BaseApiResponse<void>>(
    PATHS.getStats,
    team ? { team } : undefined
  )

const buildWindowParams = (window: PortfolioWindow) => {
  if (window.preset === "custom") {
    return { window: "custom", start: window.start, end: window.end }
  }
  return { window: window.preset }
}

export const getDashboardPortfolio = (
  window: PortfolioWindow,
  filters?: GlobalFilters,
  teamScope?: string[]
) =>
  request.api.get<PortfolioBundle>(PATHS.portfolio, {
    ...buildWindowParams(window),
    ...buildFilterParams(filters),
    ...(teamScope?.length ? { teams: teamScope.join(",") } : {})
  })

// `teamScope` narrows every tile to the given team(s) — never part of
// `filters`/`dashboard_config`, so it can never round-trip into a saved
// view or render as a FilterBar chip. Omitted/empty is the server's
// no-filter default ("every branch the caller can see"), which is correct
// only for Company HQ's own "All branches" state — every single-branch
// caller (desktop and mobile Home) must pass its one active branch's id.
//
// `configKey` names which `preferences` bag key the LIST endpoint
// resolves its widget layout from — needed because the endpoint otherwise
// always reads the account's one dedicated `dashboard_config` regardless
// of which layout the frontend just loaded. Omitted/`"dashboard_config"`
// is "existing behavior, unchanged".
export const getDashboardWidgets = (
  window: PortfolioWindow,
  filters?: GlobalFilters,
  teamScope?: string[],
  configKey?: string
) =>
  request.api.get<WidgetsResponse>(PATHS.widgets, {
    ...buildWindowParams(window),
    ...buildFilterParams(filters),
    ...(teamScope?.length ? { teams: teamScope.join(",") } : {}),
    ...(configKey && configKey !== "dashboard_config"
      ? { config_key: configKey }
      : {})
  })

export const resolveDashboardWidget = (
  instance: Pick<WidgetInstanceV4, "type" | "dataSource" | "config">,
  window: PortfolioWindow,
  filters?: GlobalFilters,
  teamScope?: string[]
) =>
  request.api.post<ResolvedWidget, BaseApiResponse<void>>(
    PATHS.resolve,
    {
      type: instance.type,
      dataSource: instance.dataSource,
      config: instance.config
    },
    {
      params: {
        ...buildWindowParams(window),
        ...buildFilterParams(filters),
        ...(teamScope?.length ? { teams: teamScope.join(",") } : {})
      }
    }
  )

// `role` scopes the catalog to a target role instead of the caller — the
// template editor authors for an audience, not for itself.
export const getDashboardCatalog = (role?: number | null) =>
  request.api.get<WidgetCatalog>(
    PATHS.catalog,
    role != null ? { role } : undefined
  )

export const getDashboardNba = () =>
  request.api.get<{ items: NbaItem[]; total_unfiltered: number }>(PATHS.nba)

export const exportDashboardPdf = (
  window: PortfolioWindow,
  filters?: GlobalFilters,
  teamScope?: string[]
) =>
  request.api.get<Blob, BaseApiResponse<void>>(
    PATHS.exportPdf,
    {
      ...buildWindowParams(window),
      ...buildFilterParams(filters),
      ...(teamScope?.length ? { teams: teamScope.join(",") } : {})
    },
    { responseType: "blob" }
  )

export const exportDashboardXlsx = (
  window: PortfolioWindow,
  filters?: GlobalFilters,
  teamScope?: string[]
) =>
  request.api.get<Blob, BaseApiResponse<void>>(
    PATHS.exportXlsx,
    {
      ...buildWindowParams(window),
      ...buildFilterParams(filters),
      ...(teamScope?.length ? { teams: teamScope.join(",") } : {})
    },
    { responseType: "blob" }
  )
